"use client";

import { createContext, useContext } from "react";

import type { SupportedTimezone } from "@marble/core/modules/timezones/constants";
import { DEFAULT_TIMEZONE } from "@marble/core/modules/timezones/constants";

/**
 * The zone the person reading the page keeps, resolved once per request on the
 * server and handed down so client components can render a time on the
 * reader's clock without each one querying for it.
 *
 * Eastern is the default only so a component rendered outside the provider
 * behaves the way it did before this existed, rather than crashing.
 */
const ViewerTimezoneContext =
  createContext<SupportedTimezone>(DEFAULT_TIMEZONE);

export function ViewerTimezoneProvider({
  timezone,
  children,
}: {
  timezone: SupportedTimezone;
  children: React.ReactNode;
}) {
  return (
    <ViewerTimezoneContext.Provider value={timezone}>
      {children}
    </ViewerTimezoneContext.Provider>
  );
}

/**
 * The reader's own zone. Use this for a time that belongs to whoever is
 * looking — their queue, their notes, when a record was created.
 *
 * Not for a time that belongs to someone else: a client's appointment renders
 * in the client's zone of care, and a business boundary like a pay period
 * stays in `COMPANY_TZ`. Those follow the subject, not the reader.
 */
export function useViewerTimezone(): SupportedTimezone {
  return useContext(ViewerTimezoneContext);
}
